import { taskLabel } from "@/lib/task-reference";
import { TaskReferenceDisplay } from "@/components/task-reference-display";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { useAssignTasksToTrip, useCarrierTasks } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { hasHttpStatus } from "@/lib/api-error";

interface AssignTasksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  dialogPresentation?: DialogProps["presentation"];
}

/**
 * Pick unassigned tasks and POST `/trips/:id/assign-tasks`. Only tasks not yet
 * on a trip are eligible; the server groups them into waypoints. Tasks come
 * from the same scoped GET the Tasks screen uses.
 */
export function AssignTasksDialog({
  open,
  onOpenChange,
  tripId,
  dialogPresentation,
}: AssignTasksDialogProps) {
  const { data, isLoading, error } = useCarrierTasks({ limit: 100 });
  const assign = useAssignTasksToTrip();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  const unassigned = (data?.data ?? []).filter((t) => t.tripId === null);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected.size === 0 || submitLock.current) return;
    submitLock.current = true;
    try {
      await assign.mutateAsync({ tripId, taskIds: Array.from(selected) });
      toast.success(
        `${selected.size} task${selected.size === 1 ? "" : "s"} assigned to trip.`
      );
      onOpenChange(false);
      setSelected(new Set());
    } catch {
      toast.error("Failed to assign tasks.");
      // also surfaced via assign.isError below
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!assign.isPending) onOpenChange(nextOpen);
      }}
      title="Assign tasks to trip"
      presentation={dialogPresentation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="max-h-72 overflow-y-auto rounded-md border divide-y">
          {isLoading && (
            <p role="status" className="p-3 text-sm text-muted-foreground">
              Loading tasks...
            </p>
          )}
          {error && (
            <p role="alert" className="p-3 text-sm text-destructive">
              {hasHttpStatus(error, 403)
                ? "You do not have permission to load unassigned tasks."
                : "Failed to load unassigned tasks. Try again."}
            </p>
          )}
          {!isLoading && !error && unassigned.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">
              No unassigned tasks available.
            </p>
          )}
          {unassigned.map((task) => (
            <label
              key={task.id}
              htmlFor={`assign-task-${task.id}`}
              className="flex cursor-pointer items-center gap-3 p-3 text-sm hover:bg-muted/50"
            >
              <Checkbox
                id={`assign-task-${task.id}`}
                aria-label={`Select task ${taskLabel(task)}`}
                checked={selected.has(task.id)}
                onCheckedChange={() => toggle(task.id)}
                disabled={assign.isPending}
              />
              <TaskReferenceDisplay
                task={task}
                className="flex-1"
                supportingText={task.type.replace(/_/g, " ").toLowerCase()}
              />
              <span className="min-w-0 max-w-24 shrink-0 break-words text-right text-xs text-muted-foreground">
                {task.dispatchCenter?.name ??
                  task.depot?.name ??
                  "Unknown Dispatch Center"}
              </span>
            </label>
          ))}
        </div>

        {assign.isError && (
          <p role="alert" className="text-sm text-destructive">
            Failed to assign tasks. They must be unassigned and in this trip's
            dispatch center.
          </p>
        )}

        {selected.size > 0 && (
          <p role="status" className="text-sm text-muted-foreground">
            {selected.size} task{selected.size === 1 ? "" : "s"} selected.
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={selected.size === 0 || assign.isPending}
          >
            {assign.isPending
              ? "Assigning..."
              : `Assign ${selected.size || ""}`.trim()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
